class PdpStickyBar extends HTMLElement {
  connectedCallback() {
    this.productInfo = this.closest('product-info');
    this.sectionId = this.dataset.sectionId;
    this.primaryButton = this.productInfo?.querySelector(`#ProductSubmitButton-${CSS.escape(this.sectionId)}`);
    this.infoWrapper = this.productInfo?.querySelector('.product__info-wrapper');
    this.onScroll = this.updateVisibility.bind(this);

    this.addEventListener('click', (event) => {
      const stickyButton = event.target.closest('.pdp-sticky-bar__button');
      if (!stickyButton) return;

      if (this.productInfo?.dataset.sizeSelectionRequired === 'true') {
        const sizePicker = this.productInfo.querySelector('.product-form__input--size');
        sizePicker?.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
        sizePicker?.querySelector('input:not(.disabled)')?.focus({ preventScroll: true });
        return;
      }

      if (!stickyButton.disabled && this.primaryButton && !this.primaryButton.disabled) {
        window.dataLayer?.push({ event: 'jp_pdp_sticky_add_to_cart' });
        this.primaryButton.click();
      }
    });

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.updateVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  updateVisibility() {
    if (!this.primaryButton || !this.infoWrapper || window.innerWidth >= 750) {
      this.hide();
      return;
    }

    const buttonRect = this.primaryButton.getBoundingClientRect();
    const infoRect = this.infoWrapper.getBoundingClientRect();
    const primaryPassedAboveViewport = buttonRect.bottom <= 0;
    const customerStillInsideProduct = infoRect.bottom > 0;

    if (primaryPassedAboveViewport && customerStillInsideProduct) this.show();
    else this.hide();
  }

  show() {
    this.removeAttribute('inert');
    this.classList.add('is-visible');
    this.setAttribute('aria-hidden', 'false');
  }

  hide() {
    this.setAttribute('inert', '');
    this.classList.remove('is-visible');
    this.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('pdp-sticky-bar')) customElements.define('pdp-sticky-bar', PdpStickyBar);

function pdpCommerceContext(productInfo, source, size = '') {
  const variantInput = productInfo?.querySelector('.product-variant-id');
  const selectedSize = size || productInfo?.querySelector('.product-form__input--size input:checked')?.value || '';
  return {
    product_id: productInfo?.dataset.productId || '',
    variant_id: variantInput?.value || productInfo?.dataset.variantId || '',
    handle: productInfo?.dataset.productHandle || '',
    price: Number(productInfo?.dataset.price || 0) / 100,
    compare_at_price: Number(productInfo?.dataset.compareAtPrice || 0) / 100,
    size: selectedSize,
    availability: variantInput ? !variantInput.disabled : productInfo?.dataset.available === 'true',
    source,
  };
}

function pushPdpEvent(event, productInfo, source, size = '') {
  window.dataLayer?.push({ event, ...pdpCommerceContext(productInfo, source, size) });
}

function initPdpSizeSelection(root = document) {
  root.querySelectorAll('product-info[data-require-size-selection="true"]').forEach((productInfo) => {
    if (productInfo.dataset.sizeSelectionInit === 'true') return;
    productInfo.dataset.sizeSelectionInit = 'true';

    const sizePicker = productInfo.querySelector('.product-form__input--size');
    const primaryButton = productInfo.querySelector('.product-form__submit');
    if (!sizePicker || !primaryButton || new URLSearchParams(window.location.search).has('variant')) return;

    const selectedSize = sizePicker.querySelector('input:checked');
    if (selectedSize) return;
    productInfo.dataset.sizeSelectionRequired = 'true';
    primaryButton.disabled = true;
    primaryButton.setAttribute('aria-disabled', 'true');
    primaryButton.querySelector('span').textContent = 'Select a size';

    const stickyBar = productInfo.querySelector('pdp-sticky-bar');
    const stickyButton = stickyBar?.querySelector('.pdp-sticky-bar__button');
    const stickyMeta = stickyBar?.querySelector('.pdp-sticky-bar__meta span:last-child');
    if (stickyButton) {
      stickyButton.removeAttribute('disabled');
      stickyButton.removeAttribute('aria-disabled');
      stickyButton.textContent = 'Select size';
      const productTitle = stickyBar.querySelector('.pdp-sticky-bar__title')?.textContent.trim();
      stickyButton.setAttribute('aria-label', productTitle ? `Choose a size for ${productTitle}` : 'Choose a size');
    }
    if (stickyMeta?.textContent.trim().startsWith('Size ')) stickyMeta.textContent = 'Select size';

    sizePicker.addEventListener('change', (event) => {
      if (!event.target.matches('input.is-size-option')) return;
      productInfo.dataset.sizeSelectionRequired = 'false';
      primaryButton.removeAttribute('aria-disabled');
      pushPdpEvent('size_selected', productInfo, 'size_picker', event.target.value);
    });
  });
}

function initPdpAnalytics(root = document) {
  root.querySelectorAll('product-info:not([data-pdp-analytics-init])').forEach((productInfo) => {
    productInfo.dataset.pdpAnalyticsInit = 'true';
    productInfo.addEventListener('click', (event) => {
      if (event.target.closest('.product-form__size-guide-trigger, .product-fit-confidence__guide-trigger')) {
        pushPdpEvent('size_guide_open', productInfo, 'purchase_rail');
      }
      if (event.target.closest('.product-concierge__button')) {
        pushPdpEvent('concierge_open', productInfo, 'fit_support');
      }
      const soldOutSize = event.target.closest('.product-form__input--size input.disabled + label');
      if (soldOutSize) {
        pushPdpEvent('sold_out_size_interest', productInfo, 'size_picker', soldOutSize.textContent.trim());
      }
    });
    productInfo.addEventListener('submit', (event) => {
      if (event.target.matches('.product-restock__form')) {
        pushPdpEvent('restock_attempt', productInfo, 'restock_form');
      } else if (event.target.matches('[data-type="add-to-cart-form"]')) {
        pushPdpEvent('add_to_cart', productInfo, 'primary_cta');
      }
    });
    if (productInfo.querySelector('.product-restock .form-status')) {
      pushPdpEvent('restock_success', productInfo, 'restock_form');
    }
  });

  if (root === document && document.documentElement.dataset.pdpRelatedAnalytics !== 'true') {
    document.documentElement.dataset.pdpRelatedAnalytics = 'true';
    document.addEventListener('click', (event) => {
      const relatedLink = event.target.closest('.related-products a[href*="/products/"]');
      if (!relatedLink) return;
      const productInfo = document.querySelector('product-info[data-product-id]');
      pushPdpEvent('related_product_click', productInfo, relatedLink.closest('[data-related-source]') ? 'collection_fallback' : 'shopify_recommendations');
    });
  }
}

function initPdpGalleryCounters(root = document) {
  root.querySelectorAll('media-gallery').forEach((gallery) => {
    if (gallery.dataset.counterInit === 'true') return;
    gallery.dataset.counterInit = 'true';
    const list = gallery.querySelector('.product__media-list');
    const current = gallery.querySelector('[data-gallery-count-current]');
    if (!list || !current) return;
    const update = () => {
      const slides = [...list.querySelectorAll('.product__media-item')];
      const midpoint = list.scrollLeft + list.clientWidth / 2;
      const index = slides.findIndex((slide) => midpoint >= slide.offsetLeft && midpoint < slide.offsetLeft + slide.offsetWidth);
      current.textContent = String(Math.max(0, index) + 1);
    };
    list.addEventListener('scroll', update, { passive: true });
    update();
  });
}

function initPdpConversion(root = document) {
  initPdpSizeSelection(root);
  initPdpAnalytics(root);
  initPdpGalleryCounters(root);
}

document.addEventListener('DOMContentLoaded', () => initPdpConversion(document));
document.addEventListener('shopify:section:load', (event) => initPdpConversion(event.target));
document.addEventListener('product-info:loaded', (event) => initPdpConversion(event.target.closest('main') || document));
